import { SelectField, TextField } from './CheckoutControls';
import { US_STATE_OPTIONS } from '@/lib/checkout/validation';
import type { AddressDraft, FieldErrors } from '@/lib/checkout/types';

interface AddressFieldsProps {
  /** `billing` or `mailing` — also the error key prefix. */
  prefix: 'billing' | 'mailing';
  value: AddressDraft;
  errors: FieldErrors;
  onChange: (patch: Partial<AddressDraft>) => void;
}

const STATE_OPTIONS = US_STATE_OPTIONS.map((state) => ({
  value: state.code,
  label: `${state.code} — ${state.name}`,
}));

export default function AddressFields({ prefix, value, errors, onChange }: AddressFieldsProps) {
  return (
    <>
      <TextField
        id={`${prefix}-street`}
        label="Street Address"
        required
        autoComplete={prefix === 'billing' ? 'billing address-line1' : 'shipping address-line1'}
        value={value.street}
        error={errors[`${prefix}.street`]}
        onChange={(event) => onChange({ street: event.target.value })}
      />
      <TextField
        id={`${prefix}-unit`}
        label="Apartment / Suite / Unit"
        optional
        autoComplete={prefix === 'billing' ? 'billing address-line2' : 'shipping address-line2'}
        value={value.unit}
        onChange={(event) => onChange({ unit: event.target.value })}
      />
      <div className="flex flex-col gap-0 sm:flex-row sm:gap-3">
        <TextField
          id={`${prefix}-city`}
          label="City"
          required
          className="sm:flex-[2]"
          autoComplete={prefix === 'billing' ? 'billing address-level2' : 'shipping address-level2'}
          value={value.city}
          error={errors[`${prefix}.city`]}
          onChange={(event) => onChange({ city: event.target.value })}
        />
        <SelectField
          id={`${prefix}-state`}
          label="State"
          required
          className="sm:flex-1"
          autoComplete={prefix === 'billing' ? 'billing address-level1' : 'shipping address-level1'}
          options={STATE_OPTIONS}
          value={value.state}
          error={errors[`${prefix}.state`]}
          onChange={(event) => onChange({ state: event.target.value })}
        />
        <TextField
          id={`${prefix}-zip`}
          label="ZIP Code"
          required
          inputMode="numeric"
          className="sm:flex-1"
          autoComplete={prefix === 'billing' ? 'billing postal-code' : 'shipping postal-code'}
          value={value.zip}
          error={errors[`${prefix}.zip`]}
          onChange={(event) => onChange({ zip: event.target.value })}
        />
      </div>
    </>
  );
}
